import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, Link, useLocation } from 'react-router-dom';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { Card, Row, Col, Form as BsForm, Alert } from 'react-bootstrap';
import { FiArrowLeft } from 'react-icons/fi';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import InternalFooter from '../components/InternalFooter/InternalFooter';
import CredentialAccessSection from './CredentialAccessSection';
import CredentialVaultNavUnlockModal from './CredentialVaultNavUnlockModal';
import {
  createCredential,
  getCredentialById,
  updateCredential,
} from '../../services/CredentialVaultService';
import { useCredentialVaultAccessSelections } from '../../hooks/useCredentialVaultAccessSelections';
import { encryptCredential } from '../../crypto/credentialVaultCrypto';
import {
  buildCredentialVaultListLocation,
  credentialVaultListReturnState,
  useCredentialVaultCrypto,
} from './CredentialVaultCryptoProvider';
import '../settings/commonstyle.css';
import '../users/userlist.css';
import '../users/userform.css';
import '../tasks/tasklist.css';
import '../tasks/addtask.css';
import './credentialVault.css';

const PRIORITY_OPTS = Array.from({ length: 10 }, (_, i) => i + 1);

const prioritySchema = Yup.string()
  .nullable()
  .test('priority-range', 'Choose a priority between 1 and 10', (v) => {
    if (v == null || v === '') return true;
    const n = parseInt(v, 10);
    return Number.isFinite(n) && n >= 1 && n <= 10;
  });

const buildSchema = (isEdit) =>
  Yup.object().shape({
    title: Yup.string().trim().required('Title is required'),
    user_name: Yup.string().trim().required('User name is required'),
    password: isEdit ? Yup.string() : Yup.string().required('Password is required'),
    website: Yup.string().trim(),
    provider_name: Yup.string().trim(),
    priority: prioritySchema,
    credential_scope: isEdit
      ? Yup.string()
      : Yup.string()
          .oneOf(['corporate', 'personal'], 'Choose corporate or personal')
          .required('Choose whether this credential is corporate or personal'),
    corporate_private: Yup.boolean(),
  });

const emptyValues = () => ({
  title: '',
  website: '',
  provider_name: '',
  user_name: '',
  password: '',
  priority: '',
  credential_scope: 'corporate',
  corporate_private: false,
});

const CORPORATE_CREDENTIAL_LIST = '/credential-vault/credentialVaultList';
const PERSONAL_CREDENTIAL_LIST = '/credential-vault/personalVaultList';

function listPathForCredentialScope(scope) {
  return scope === 'personal' ? PERSONAL_CREDENTIAL_LIST : CORPORATE_CREDENTIAL_LIST;
}

function appendAccessAndGrants(payload, sel, includeSharing) {
  if (!includeSharing) return;
  payload.user_ids = JSON.stringify(sel.selectedUsers || []);
  const grants = (sel.selectedUsers || []).map((id) => ({
    corporate_user_id: id,
    access_level: sel.userAccessById?.[id] ?? sel.userAccessById?.[String(id)] ?? 'view',
  }));
  payload.user_grants = JSON.stringify(grants);

  const branches = sel.selectedBranches || [];
  const depts = sel.selectedDepartments || [];
  const subs = sel.selectedSubDepartments || [];

  /** Only the narrowest selected org tier is sent (matches server: sub-dept overrides dept and branch). */
  let branchIds = [];
  let departmentIds = [];
  let subDepartmentIds = [];
  let branchGrants = [];
  let departmentGrants = [];
  let subDeptGrants = [];

  if (subs.length > 0) {
    subDepartmentIds = subs;
    subDeptGrants = subs.map((id) => ({
      sub_department_id: id,
      access_level: sel.subDepartmentAccessById?.[id] ?? sel.subDepartmentAccessById?.[String(id)] ?? 'view',
    }));
  } else if (depts.length > 0) {
    departmentIds = depts;
    departmentGrants = depts.map((id) => ({
      department_id: id,
      access_level: sel.departmentAccessById?.[id] ?? sel.departmentAccessById?.[String(id)] ?? 'view',
    }));
  } else {
    branchIds = branches;
    branchGrants = branches.map((id) => ({
      branch_id: id,
      access_level: sel.branchAccessById?.[id] ?? sel.branchAccessById?.[String(id)] ?? 'view',
    }));
  }

  payload.branch_ids = JSON.stringify(branchIds);
  payload.department_ids = JSON.stringify(departmentIds);
  payload.sub_department_ids = JSON.stringify(subDepartmentIds);
  payload.branch_grants = JSON.stringify(branchGrants);
  payload.department_grants = JSON.stringify(departmentGrants);
  payload.sub_department_grants = JSON.stringify(subDeptGrants);
}

const CredentialVaultForm = () => {
  const { id: editId } = useParams();
  const isEdit = Boolean(editId);
  const navigate = useNavigate();
  const credentialListPathRef = useRef(CORPORATE_CREDENTIAL_LIST);
  const [initial, setInitial] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [accessSnapshot, setAccessSnapshot] = useState(null);
  const [showCredentialPassword, setShowCredentialPassword] = useState(false);

  const access = useCredentialVaultAccessSelections();
  const { metadataLoaded, hydrateFromCredential } = access;
  const hydratedEditIdRef = useRef(null);
  const { vaultInitialized, personalVaultInitialized } = useCredentialVaultCrypto();

  const [submitUnlockOpen, setSubmitUnlockOpen] = useState(false);
  const [submitUnlockSide, setSubmitUnlockSide] = useState('corporate');
  const pendingSaveRef = useRef(null);

  const location = useLocation();

  const canManageSharing = (!isEdit || accessSnapshot?.can_manage_sharing === true);

  useEffect(() => {
    hydratedEditIdRef.current = null;
  }, [editId]);

  useEffect(() => {
    if (!isEdit || !accessSnapshot || !metadataLoaded) return;
    if (hydratedEditIdRef.current === editId) return;
    hydratedEditIdRef.current = editId;
    hydrateFromCredential(accessSnapshot);
  }, [isEdit, editId, accessSnapshot, metadataLoaded, hydrateFromCredential]);

  useEffect(() => {
    if (isEdit) return;
    const side = location.state?.credentialVaultSide;
    if (side !== 'corporate' && side !== 'personal') {
      toast.warn('Use Add credential from the Corporate credentials or Personal credentials list.', {
        position: 'top-right',
        autoClose: 5000,
      });
      navigate('/credential-vault/credentialVaultList', { replace: true });
      return;
    }
    const v = emptyValues();
    v.credential_scope = side;
    credentialListPathRef.current = listPathForCredentialScope(side);
    setInitial(v);
    /* Do not call setSearchParams(..., { replace }) here — it replaces the history entry and clears
       location.state, which triggers this effect again and redirects back to the list. */
  }, [isEdit, location.state?.credentialVaultSide, navigate]);

  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const res = await getCredentialById(editId);
        if (res.data?.success && res.data.data) {
          const d = res.data.data;
          if (d.can_edit_credential === false) {
            setLoadError('You do not have permission to edit this Credential');
            return;
          }
          const listOrigin = String(d.credential_list_origin || (d.is_private ? 'personal' : 'corporate')).toLowerCase();
          const isCorporatePrivate = !!d.is_private && listOrigin === 'corporate';
          const scope = d.is_private && !isCorporatePrivate ? 'personal' : 'corporate';
          credentialListPathRef.current = listPathForCredentialScope(scope);
          setInitial({
            title: d.title || '',
            website: d.website || '',
            provider_name: d.provider_name || '',
            user_name: d.user_name || '',
            password: '',
            priority:
              d.priority != null && d.priority !== '' && Number(d.priority) >= 1
                ? String(d.priority)
                : '',
            credential_scope: scope,
            corporate_private: isCorporatePrivate,
          });
          setAccessSnapshot({
            branch_ids: d.branch_ids || [],
            department_ids: d.department_ids || [],
            sub_department_ids: d.sub_department_ids || [],
            branch_grants: d.branch_grants || [],
            department_grants: d.department_grants || [],
            sub_department_grants: d.sub_department_grants || [],
            user_ids: d.user_ids || [],
            user_grants: d.user_grants || [],
            can_manage_sharing: !!d.can_manage_sharing,
            can_edit_credential: !!d.can_edit_credential,
          });
        } else {
          setLoadError('Credential not found');
        }
      } catch (e) {
        setLoadError(e?.response?.data?.message || 'Failed to load');
      }
    })();
  }, [editId, isEdit]);

  const goBackToList = () => {
    const path = credentialListPathRef.current;
    const side = path === PERSONAL_CREDENTIAL_LIST ? 'personal' : 'corporate';
    navigate({
      ...buildCredentialVaultListLocation(path, location.state),
      state: credentialVaultListReturnState(side),
    });
  };

  const handleSubmitUnlockHide = () => {
    setSubmitUnlockOpen(false);
    pendingSaveRef.current = null;
  };

  const handleSubmitUnlockContinue = ({ dekBytes }) => {
    setSubmitUnlockOpen(false);
    const pending = pendingSaveRef.current;
    pendingSaveRef.current = null;
    if (!pending?.values || !dekBytes) return;
    const { values, setSubmitting } = pending;
    setSubmitting(true);
    void (async () => {
      try {
        if (values.credential_scope === 'personal') {
          await saveCredentialPayload(values, null, dekBytes);
        } else {
          await saveCredentialPayload(values, dekBytes, null);
        }
      } catch (e) {
        console.error(e);
        toast.error(e?.response?.data?.message || 'Could not save Credential');
      } finally {
        setSubmitting(false);
      }
    })();
  };

  const saveCredentialPayload = async (values, corporateDek, personalDekForEncrypt) => {
    const isPrivate = !!values.is_private;
    const pr =
      values.priority === '' || values.priority == null ? 0 : parseInt(values.priority, 10);
    const payload = {
      title: values.title,
      website: values.website || null,
      provider_name: values.provider_name || null,
      user_name: values.user_name,
      priority: Number.isFinite(pr) && pr >= 1 && pr <= 10 ? pr : 0,
      is_private: isPrivate,
    };

    if (!isEdit) {
      payload.credential_list_origin =
        values.credential_list_origin ||
        (values.credential_scope === 'personal' ? 'personal' : 'corporate');
    }

    const includeSharing = !!canManageSharing && !isPrivate && access.metadataLoaded;
    appendAccessAndGrants(payload, access, includeSharing);

    const encryptWithPersonalVault = values.credential_scope === 'personal';

    if (encryptWithPersonalVault) {
      if (!personalDekForEncrypt) {
        throw new Error('Personal vault encryption key is required');
      }
      const pwd = values.password != null ? String(values.password) : '';
      if (!isEdit || pwd.length > 0) {
        const enc = await encryptCredential(pwd, personalDekForEncrypt);
        payload.encrypted_password = enc.data;
        payload.password_iv = enc.iv;
      }
    } else if (vaultInitialized && corporateDek) {
      const pwd = values.password != null ? String(values.password) : '';
      if (!isEdit || pwd.length > 0) {
        const enc = await encryptCredential(pwd, corporateDek);
        payload.encrypted_password = enc.data;
        payload.password_iv = enc.iv;
      }
    } else if (!vaultInitialized) {
      if (!isEdit) {
        payload.password = values.password;
      } else if (values.password && String(values.password).length) {
        payload.password = values.password;
      }
    }

    if (!isEdit) {
      await createCredential(payload);
      toast.success('New Credential added to your Credentials Vault');
    } else {
      await updateCredential(editId, payload);
      toast.success('Credential updated in your Credentials Vault');
    }
    goBackToList();
  };

  const pageTitleText = isEdit ? 'Edit Credential' : 'Add a new Credential';

  const renderPageTitleInsideCard = (
    <div className="credential-vault-entry-page-title credential-vault-entry-page-title--in-card text-start w-100">
      <h4 className="credential-vault-entry-page-heading mb-0">{pageTitleText}</h4>
    </div>
  );

  const renderChrome = (children) => (
    <div className="credential-vault-form-page credential-vault-entry-form-page">
      <ToastContainer />
      <BsForm className="d-flex flex-column align-items-stretch mb-0 credential-vault-entry-page-top">
        <button type="button" className="back-button align-self-start mb-2" onClick={goBackToList}>
          <FiArrowLeft style={{ marginRight: '0.5rem' }} />
          Back
        </button>
      </BsForm>
      <Row className="g-4 mb-0">
        <Col xl={12}>{children}</Col>
      </Row>
      <InternalFooter />
    </div>
  );

  if (isEdit && !initial && !loadError) {
    return renderChrome(
      <Card className="credential-vault-entry-form-card card">
        <Card.Body
          className="p-4 credential-vault-entry-form-card-body credential-vault-entry-card-body-loading d-flex flex-column"
          style={{ minHeight: '400px' }}
        >
          {renderPageTitleInsideCard}
          <div className="credential-form-loading flex-grow-1 d-flex align-items-center justify-content-center py-5">
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p className="text-muted small mt-3 mb-0">Loading…</p>
            </div>
          </div>
        </Card.Body>
      </Card>
    );
  }

  if (loadError) {
    return renderChrome(
      <Card className="credential-vault-entry-form-card card">
        <Card.Body className="p-4 credential-vault-entry-form-card-body">
          {renderPageTitleInsideCard}
          <p className="text-danger mb-3">{loadError}</p>
          <div className="text-end task-form-toolbar toolbar toolbar-bottom pt-3">
            <button type="button" className="btn btn-cancel task-form-toolbar-btn" onClick={goBackToList}>
              Back to list
            </button>
          </div>
        </Card.Body>
      </Card>
    );
  }

  return renderChrome(
    <>
      <Card className="credential-vault-entry-form-card card shadow-sm">
        <Card.Body className="p-4 credential-vault-entry-form-card-body">
          {renderPageTitleInsideCard}
          <Formik
          initialValues={initial || emptyValues()}
          enableReinitialize
          validationSchema={buildSchema(isEdit)}
          onSubmit={async (values, { setSubmitting }) => {
            const withPrivate = {
              ...values,
              is_private: values.credential_scope === 'personal' || !!values.corporate_private,
              credential_list_origin: values.credential_scope === 'personal' ? 'personal' : 'corporate',
            };

            if (withPrivate.credential_scope === 'personal') {
              if (!personalVaultInitialized) {
                toast.error('Create your personal vault before saving this credential.');
                setSubmitting(false);
                return undefined;
              }
              pendingSaveRef.current = { values: withPrivate, setSubmitting };
              setSubmitUnlockSide('personal');
              setSubmitUnlockOpen(true);
              setSubmitting(false);
              return undefined;
            }

            if (!vaultInitialized) {
              toast.error('Corporate Credentials Vault is not available.');
              setSubmitting(false);
              return undefined;
            }

            pendingSaveRef.current = { values: withPrivate, setSubmitting };
            setSubmitUnlockSide('corporate');
            setSubmitUnlockOpen(true);
            setSubmitting(false);
            return undefined;
          }}
        >
          {({ values, isSubmitting, setFieldValue }) => {
            credentialListPathRef.current = listPathForCredentialScope(values.credential_scope);
            const isPersonal = values.credential_scope === 'personal';
            const corporatePrivate = !!values.corporate_private;
            const showAccessControlSection =
              !isPersonal &&
              ((!isEdit && values.credential_scope === 'corporate') ||
                (isEdit && canManageSharing && !corporatePrivate));
            return (
            <Form>
              <section>
                <Row className="mb-2">
                  <Col xs={12}>
                    {!isEdit ? (
                      <p className="mb-2 small text-muted">
                        <strong>Type:</strong>{' '}
                        {values.credential_scope === 'personal'
                          ? 'Personal — private to you (opened from Personal credentials).'
                          : corporatePrivate
                            ? 'Corporate — private to you (same organization vault as shared credentials; others cannot access).'
                            : 'Corporate — shared with your organization (opened from Corporate credentials).'}
                      </p>
                    ) : (
                      <p className="mb-0 small text-muted">
                        <strong>Type:</strong>{' '}
                        {isPersonal
                          ? 'Personal (private)'
                          : corporatePrivate
                            ? 'Corporate — private to you'
                            : 'Corporate (shared)'}{' '}
                        — cannot be changed after creation.
                      </p>
                    )}
                    {!isEdit && isPersonal && !personalVaultInitialized ? (
                      <Alert variant="warning" className="mt-2 mb-0 py-2 small">
                        Create your personal vault under{' '}
                        <Link to="/credential-vault/vaultSettings">Vault settings</Link>
                        {' '}before you can save personal credentials.
                      </Alert>
                    ) : null}
                  </Col>
                </Row>
                <Row className="align-items-start">
                  <Col lg={6} className="mb-2">
                    <div className="form-group mb-3">
                      <label htmlFor="credential-title" className="text-label">
                        Title*
                      </label>
                      <Field id="credential-title" name="title" className="form-control" placeholder="Short title" />
                      <div className="credential-vault-field-feedback">
                        <ErrorMessage name="title" component="div" className="text-danger small" />
                      </div>
                    </div>
                  </Col>
                  <Col lg={6} className="mb-2">
                    <div className="form-group mb-3">
                      <label htmlFor="credential-priority" className="text-label">
                        Priority
                      </label>
                      <Field id="credential-priority" name="priority" as="select" className="form-control form-select">
                        <option value="">— Select —</option>
                        {PRIORITY_OPTS.map((n) => (
                          <option key={n} value={String(n)}>
                            {n}
                          </option>
                        ))}
                      </Field>
                      <div className="credential-vault-field-feedback">
                        <ErrorMessage name="priority" component="div" className="text-danger small" />
                      </div>
                    </div>
                  </Col>
                </Row>
                <Row>
                  <Col lg={6} className="mb-2">
                    <div className="form-group mb-3">
                      <label htmlFor="credential-website" className="text-label">
                        Website
                      </label>
                      <Field id="credential-website" name="website" className="form-control" placeholder="https://…" />
                    </div>
                  </Col>
                  <Col lg={6} className="mb-2">
                    <div className="form-group mb-3">
                      <label htmlFor="credential-provider" className="text-label">
                        Provider Name
                      </label>
                      <Field
                        id="credential-provider"
                        name="provider_name"
                        className="form-control"
                        placeholder="e.g. Google, AWS"
                      />
                    </div>
                  </Col>
                </Row>
                <Row>
                  <Col lg={6} className="mb-2">
                    <div className="form-group mb-3">
                      <label htmlFor="credential-user_name" className="text-label">
                        User Name*
                      </label>
                      <Field
                        id="credential-user_name"
                        name="user_name"
                        className="form-control"
                        autoComplete="username"
                        placeholder="Email or username"
                      />
                      <ErrorMessage name="user_name" component="div" className="text-danger small" />
                    </div>
                  </Col>
                  <Col lg={6} className="mb-2">
                    <div className="form-group mb-3">
                      <label htmlFor="credential-password" className="text-label">
                        Password{isEdit ? '' : '*'}
                      </label>
                      <div className="position-relative password-input-wrapper">
                        <Field
                          id="credential-password"
                          name="password"
                          type={showCredentialPassword ? 'text' : 'password'}
                          className="form-control"
                          autoComplete="new-password"
                          placeholder={isEdit ? 'Unchanged if empty' : 'Required'}
                        />
                        <button
                          type="button"
                          className="password-toggle-btn"
                          onClick={() => setShowCredentialPassword((v) => !v)}
                          aria-label={showCredentialPassword ? 'Hide password' : 'Show password'}
                        >
                          <i className={`fas ${showCredentialPassword ? 'fa-eye-slash' : 'fa-eye'}`} aria-hidden />
                        </button>
                      </div>
                      <ErrorMessage name="password" component="div" className="text-danger small" />
                    </div>
                  </Col>
                </Row>

              </section>

              {showAccessControlSection ? (
                <CredentialAccessSection
                  prepend={
                    !isEdit && values.credential_scope === 'corporate' ? (
                      <BsForm.Check
                        type="checkbox"
                        id="credential-corporate-private"
                        label="Private to me (not shared; still encrypted with the corporate Credentials Vault)"
                        checked={corporatePrivate}
                        onChange={(e) => setFieldValue('corporate_private', e.target.checked)}
                      />
                    ) : null
                  }
                  showSharingControls={canManageSharing && !corporatePrivate}
                  branches={access.branches}
                  departments={access.departments}
                  subDepartments={access.subDepartments}
                  filteredDepartments={access.filteredDepartments}
                  filteredSubDepartments={access.filteredSubDepartments}
                  selectedBranches={access.selectedBranches}
                  selectedDepartments={access.selectedDepartments}
                  selectedSubDepartments={access.selectedSubDepartments}
                  branchAccessById={access.branchAccessById}
                  departmentAccessById={access.departmentAccessById}
                  subDepartmentAccessById={access.subDepartmentAccessById}
                  onBranchAccessLevelChange={access.handleBranchAccessLevelChange}
                  onDepartmentAccessLevelChange={access.handleDepartmentAccessLevelChange}
                  onSubDepartmentAccessLevelChange={access.handleSubDepartmentAccessLevelChange}
                  selectedUsers={access.selectedUsers}
                  userAccessById={access.userAccessById}
                  onUserAccessLevelChange={access.handleUserAccessLevelChange}
                  onBranchesChange={(ids) => access.handleBranchChange(ids)}
                  onDepartmentsChange={(ids) => access.handleDepartmentChange(ids)}
                  onSubDepartmentsChange={(ids) => access.handleSubDepartmentChange(ids)}
                  onUsersChange={(ids) => access.handleUserChange(ids)}
                  availableUsers={access.usersForCredentialSelect}
                />
              ) : null}

              <div className="task-form-toolbar text-end toolbar toolbar-bottom pt-4">
                <button
                  type="button"
                  className="btn btn-cancel task-form-toolbar-btn me-2"
                  disabled={isSubmitting}
                  onClick={goBackToList}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary sw-btn-next task-form-toolbar-btn"
                  disabled={
                    isSubmitting ||
                    (canManageSharing && !isPersonal && !corporatePrivate && !access.metadataLoaded) ||
                    (!isEdit && isPersonal && !personalVaultInitialized)
                  }
                >
                  {isSubmitting ? (
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                  ) : null}
                  {isEdit ? 'Save' : 'Submit'}
                </button>
              </div>
            </Form>
            );
          }}
        </Formik>
      </Card.Body>
    </Card>
    <CredentialVaultNavUnlockModal
      show={submitUnlockOpen}
      side={submitUnlockSide === 'personal' ? 'personal' : 'corporate'}
      purpose="save"
      onHide={handleSubmitUnlockHide}
      onContinue={handleSubmitUnlockContinue}
    />
    </>
  );
};

export default CredentialVaultForm;
